import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getRoot } from 'lexical';
import type { FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';

type ValueSyncPluginProps = {
    readonly value: string | undefined;
};

export const ValueSyncPlugin: FunctionComponent<ValueSyncPluginProps> = ({ value }) => {
    const [editor] = useLexicalComposerContext();
    const isFirstRender = useRef(true);
    const lastSyncedValue = useRef<string | undefined>(value);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;

            return;
        }

        if (value === lastSyncedValue.current) {
            return;
        }

        editor.update(
            () => {
                const currentHtml = $generateHtmlFromNodes(editor, null);

                if (currentHtml === value) {
                    lastSyncedValue.current = value;

                    return;
                }

                const root = $getRoot();

                root.clear();

                if (value === undefined || value === '') {
                    root.append($createParagraphNode());
                    lastSyncedValue.current = value;

                    return;
                }

                const parser = new DOMParser();
                const dom = parser.parseFromString(value, 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);

                try {
                    root.append(...nodes);
                } catch {
                    root.append($createParagraphNode().append(...nodes));
                }

                lastSyncedValue.current = value;
            },
            { tag: 'external-sync' },
        );
    }, [editor, value]);

    return null;
};
